import { SpreadsheetData } from "@components/spreadsheet/types";
import { calculateDebitAndCreditTotals } from "@components/accounting/journalEntryDrawer/JournalEntryDrawerUtils";
import { JournalEntryLineProxyObject } from "@components/accounting/journalEntryDrawer/JournalEntryDrawer";
import { RecurrenceFormValues } from "@components/accounting/journalEntryDrawer/RecurrenceModalContent";
import { getDateByRecurringRule } from "@common/helpers/recurringJournalEntries";
import { ManualJournalRecurringRule } from "@common/types/domains/accounting/manualJournalRecurringRule";
import { getDaySuffix } from "@common/helpers/date";

export const generateDayOptions = (month: number | null | undefined) => {
    if (month === null || month === undefined) {
        return [];
    }

    const daysInMonth = new Date(
        new Date().getFullYear(),
        month + 1,
        0,
    ).getDate();

    return Array.from({ length: daysInMonth }, (_, i) => ({
        label: `${i + 1}${getDaySuffix(i + 1)}`,
        value: i + 1,
    }));
};

type AmountField = "debitAmount" | "creditAmount";

const parsePositiveAmount = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return null;
    }

    return numericValue;
};

export function changeDataAfterAmountChanged(
    data: SpreadsheetData,
    rowIdx: number,
    field: AmountField,
) {
    const amount = parsePositiveAmount(data[rowIdx][field]);
    const oppositeField = getOppositeAmountField(field);
    const hasOppositeAmountValue =
        parsePositiveAmount(data[rowIdx][oppositeField]) !== null;

    if (amount === null) {
        return [
            ...data.slice(0, rowIdx),
            { ...data[rowIdx], [field]: null },
            ...data.slice(rowIdx + 1),
        ];
    }

    if (hasOppositeAmountValue) {
        return [
            ...data.slice(0, rowIdx),
            {
                ...data[rowIdx],
                [field]: amount,
                [oppositeField]: null,
            },
            ...data.slice(rowIdx + 1),
        ];
    }

    return [
        ...data.slice(0, rowIdx),
        { ...data[rowIdx], [field]: amount },
        ...data.slice(rowIdx + 1),
    ];
}

function getOppositeAmountField(field: AmountField): AmountField {
    return field === "debitAmount" ? "creditAmount" : "debitAmount";
}

export function changeDataAfterAccountChanged(
    data: SpreadsheetData,
    rowIdx: number,
    memo: string,
) {
    const { accountId, debitAmount, creditAmount, description } = data[rowIdx];

    const shouldAutoPopulateAmount =
        !!accountId && !debitAmount && !creditAmount;
    const shouldAutoPopulateDescription = !!accountId && !description;
    if (!shouldAutoPopulateAmount && !shouldAutoPopulateDescription) {
        return data;
    }
    const newLine = { ...data[rowIdx] };

    if (shouldAutoPopulateAmount) {
        newLine.debitAmount = null;
        newLine.creditAmount = null;
        const dataWithNulledRow = [
            ...data.slice(0, rowIdx),
            newLine,
            ...data.slice(rowIdx + 1),
        ];
        const linesSum = calculateDebitAndCreditTotals(
            dataWithNulledRow as JournalEntryLineProxyObject[],
        );
        const difference = linesSum.debitAmount - linesSum.creditAmount;

        if (difference > 0) {
            newLine.creditAmount = difference;
        } else if (difference !== 0) {
            newLine.debitAmount = Math.abs(difference);
        }
    }

    if (shouldAutoPopulateDescription) {
        const uniqueDescriptionsInOtherLines = new Set(
            data.map((currentLine) => currentLine.description).filter(Boolean),
        );
        if (uniqueDescriptionsInOtherLines.size === 0) {
            if (memo.length > 0) {
                newLine.description = memo;
            }
        }
        if (uniqueDescriptionsInOtherLines.size === 1) {
            newLine.description =
                uniqueDescriptionsInOtherLines.values().next().value ?? "";
        }
    }

    return [...data.slice(0, rowIdx), newLine, ...data.slice(rowIdx + 1)];
}

export const getFirstOccurrenceDate = (
    formValues: RecurrenceFormValues,
    startDate: string,
): Date => {
    const date = new Date(startDate);
    const { period, interval } = formValues;
    if (!period || !interval) {
        return date;
    }

    return getDateByRecurringRule(
        {
            ...formValues,
            period,
            interval,
        } as ManualJournalRecurringRule,
        date,
    );
};
